import React, { useEffect, useRef } from 'react';
import { useVoiceDemo } from '../utils/useExclusiveAudio';
import { useTessaWebCall } from '../utils/useTessaWebCall';
import { Link } from 'react-router-dom';
import { FiArrowUpRight, FiArrowDown, FiPhone, FiMic, FiCheck } from 'react-icons/fi';

const PHONE_DISPLAY = '(424) 434-4541';
const PHONE_TEL = 'tel:+14244344541';

function track(event, location) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', event, { location });
  }
  if (event === 'demo_call_click' && typeof window !== 'undefined' && window.fbq) {
    window.fbq('trackCustom', 'DemoCallClick', { location });
  }
}

function Hero() {
  const { open: taraOpen, toggle: toggleTara } = useVoiceDemo();
  const webCall = useTessaWebCall({ onStart: () => track('web_call_start', 'hero-orb') });
  const callActive = webCall.status === 'live' || webCall.status === 'connecting';
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    // muted is a DOM property; reinforce it after hydrating the captured HTML.
    video.defaultMuted = true;
    video.muted = true;
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updatePlayback = () => {
      if (preference.matches) {
        video.pause();
      } else {
        video.play().catch(() => {}); // Keep the poster if autoplay is blocked.
      }
    };
    updatePlayback();
    preference.addEventListener('change', updatePlayback);
    return () => preference.removeEventListener('change', updatePlayback);
  }, []);

  const toggleVideo = () => {
    const video = videoRef.current;
    if (video.paused) video.play().catch(() => {});
    else {
      video.pause();
    }
  };

  return (
    <section className="studio-hero" id="hero" aria-labelledby="hero-heading">
      <video ref={videoRef} className="studio-hero-video" loop muted playsInline preload="metadata" poster="/hero/hero-poster.jpg" aria-hidden="true">
        <source src="/hero/hero-720.mp4" type="video/mp4" />
      </video>
      <div className="studio-hero-shade" aria-hidden="true" />
      <div className="container">
        <div className="studio-hero-grid">
          <div className="studio-hero-copy">
            <span className="studio-eyebrow"><span className="studio-status-dot" /> Built in LA. Always on.</span>
            <h1 id="hero-heading">Call this number.<br /><span>An AI picks up.</span></h1>
            <p className="studio-hero-description">
              UBOTIKA builds AI receptionists for Los Angeles businesses.
              Answer calls 24/7, book appointments, and follow up with customers —
              connected to the tools you already use.
            </p>
            <div className="studio-hero-actions">
              <button
                type="button"
                className="studio-button"
                aria-expanded={taraOpen}
                aria-controls="hero-voice-demo"
                onClick={() => {
                  toggleTara();
                  track('tara_open', 'hero');
                }}
              >
                <FiMic aria-hidden="true" /> {taraOpen ? 'Close voice demo' : 'Talk to our AI'} <FiArrowUpRight aria-hidden="true" />
              </button>
              <Link to="/free-missed-lead-audit" className="studio-text-link">Get my free audit <FiArrowUpRight aria-hidden="true" /></Link>
            </div>
            <p className="studio-hero-note">30 seconds. No signup. A real conversation.</p>
            <div id="hero-voice-demo" hidden={!taraOpen} className="studio-voice-widget">
              {taraOpen && <>
                <p>Start a conversation below, or <a href={PHONE_TEL} onClick={() => track('demo_call_click', 'hero-fallback')}>call the demo line</a>.</p>
                <elevenlabs-convai agent-id="agent_0701k6238fn5ex58es31xvqvs868"></elevenlabs-convai>
              </>}
            </div>
            <div className="studio-pilot-note"><FiCheck aria-hidden="true" /><span><strong>2 weeks free.</strong> Then from $500/month.</span></div>
          </div>

          <div className="studio-demo-card">
            <div className="studio-demo-top"><span><span className="studio-status-dot" /> Interactive voice demo</span><span>01 / VOICE</span></div>
            <button
              type="button"
              className={`studio-orbit studio-orbit-button is-${webCall.status}`}
              onClick={callActive ? webCall.end : webCall.start}
              aria-label={callActive ? 'End voice chat with Tessa' : 'Talk to Tessa in your browser'}
            >
              <span className="studio-orbit-ring studio-orbit-ring-outer" aria-hidden="true" />
              <span className="studio-orbit-ring studio-orbit-ring-inner" aria-hidden="true" />
              <span className="studio-orbit-core" aria-hidden="true">
                <span className="studio-waveform">{[18, 30, 47, 64, 38, 74, 52, 31, 18].map((height, index) => <span key={index} style={{ '--bar-height': height + 'px', '--bar-delay': index * -0.17 + 's' }} />)}</span>
              </span>
              <span className="studio-orbit-label" aria-live="polite">
                {webCall.status === 'connecting' ? 'Connecting to Tessa…'
                  : webCall.status === 'live' ? 'Tessa is listening · tap to hang up'
                  : 'Tap to talk to Tessa in your browser'}
              </span>
            </button>
            {webCall.status === 'error' && <p className="studio-orbit-error" role="alert">{webCall.error}</p>}
            <div className="studio-demo-info">
              <div className="studio-demo-identity"><span className="studio-demo-avatar">T</span><div><h2>Meet Tessa.</h2><p>Your AI receptionist · Available 24/7</p></div></div>
              <a href={PHONE_TEL} className="studio-demo-phone" onClick={() => track('demo_call_click', 'hero')}><span>{PHONE_DISPLAY}</span><FiPhone aria-hidden="true" /></a>
              <p>Go ahead. Give her a call.</p>
            </div>
            <div className="studio-demo-capabilities"><span><FiCheck aria-hidden="true" /> Answers calls</span><span><FiCheck aria-hidden="true" /> Books appointments</span></div>
          </div>
        </div>
        <div className="studio-hero-footer">
          <a href="#services" className="studio-explore">Explore what&apos;s possible <FiArrowDown aria-hidden="true" /></a>
          <button type="button" className="studio-video-toggle" onClick={toggleVideo}>
            Play / pause background video
          </button>
          <p>AI receptionists for Los Angeles businesses</p>
        </div>
        <div className="studio-proof" aria-label="UBOTIKA at a glance">
          <div><strong>24/7</strong><span>Every call. Every day.</span></div>
          <div><strong>50+</strong><span>LA businesses trust us</span></div>
          <div><strong>$17K</strong><span>Average annual savings</span></div>
          <div><strong>2 weeks</strong><span>Try it on your own line</span></div>
        </div>
      </div>
    </section>
  );
}

export default Hero;
